import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Alert, AlertTag } from '../types/index.js';  // Use index.ts which has P1.15 fields
import { StatusType } from '../types/types.js';
import { fetchAlerts } from '../../api/alertApi';
import { useAuth } from '../../context/AuthContext.js';
import { useTenant } from './TenantContext.js';
import { getBaseUrl } from '../utils/apiConfig.js';
import { executiveDashboardService } from '../services/executiveDashboardService.js';
import { secureTokenService } from '../services/secureTokenService.js';

interface SecurityDataContextType {
  alerts: Alert[];
  tags: AlertTag[];
  loading: boolean;
  error: Error | null;
  mlAnalysis: {
    confidence: number;
    recommendations: string[];
    performance: {
      accuracy: number;
      precision: number;
      recall: number;
    };
    mitre_techniques?: Array<{
      id: string;
      name: string;
      confidence: number;
      severity: string;
    }>;
    source?: 'api' | 'fallback' | 'mock';
    timestamp?: string;
  } | null;
  assetContext: {
    id: string;
    name: string;
    owner: string;
    department: string;
    riskScore: number;
  } | null;
  refreshAlerts: () => Promise<void>;
}

const SecurityDataContext = createContext<SecurityDataContextType>({
  alerts: [],
  tags: [
    { id: "t1", name: "Critical", color: "#FF0000" },
    { id: "t2", name: "High Priority", color: "#FF0000" },
    { id: "t3", name: "Credential Access", color: "#FFA500" }
  ],
  loading: false,
  error: null,
  mlAnalysis: null,
  assetContext: null,
  refreshAlerts: async () => {}
});

const mockAlerts: Alert[] = [
  {
    id: 'alert-1',
    title: 'Suspicious PowerShell Activity',
    severity: 'high',
    status: StatusType.ACTIVE,
    timestamp: '2025-05-01T10:00:00Z',
    description: 'Detected suspicious PowerShell command execution with potential malicious intent',
    affectedAsset: 'DESKTOP-ABC123',
    assignedTo: 'john.doe@example.com',
    tags: [
      { id: "t4", name: "T1059.001", color: "#4444FF" },
      { id: "t5", name: "PowerShell", color: "#0000FF" }
    ],
    details: {
      source: 'Windows Event Log',
      affectedHost: 'DESKTOP-ABC123',
      technique: 'T1059.001 - PowerShell',
      confidence: 0.85,
      description: 'Suspicious PowerShell execution detected with encoded commands'
    },
    // P1.15 Internal Sources Context (for testing)
    user_context: {
      username: 'jdoe',
      display_name: 'John Doe',
      email: 'john.doe@example.com',
      department: 'Engineering',
      title: 'Senior DevOps Engineer',
      manager: 'Jane Smith',
      location: 'San Francisco, CA',
      employee_type: 'Full-time',
      account_status: 'active',
      groups: ['Domain Admins', 'DevOps', 'Engineering'],
      is_privileged: true,
      enriched_at: '2025-05-01T10:00:00Z',
      source: 'Active Directory'
    },
    asset_context: {
      hostname: 'DESKTOP-ABC123',
      asset_id: 'AST-12345',
      asset_type: 'Workstation',
      criticality: 'high',
      owner: 'John Doe',
      support_group: 'IT Operations',
      location: 'San Francisco Office',
      environment: 'Production',
      os: 'Windows 11 Enterprise',
      last_patched: '2025-04-28T00:00:00Z',
      business_service: 'DevOps Infrastructure',
      enriched_at: '2025-05-01T10:00:00Z',
      source: 'ServiceNow CMDB'
    },
    network_context: {
      ip_address: '10.50.100.45',
      subnet: '10.50.100.0/24',
      vlan: 'VLAN-100',
      network_zone: 'internal',
      location: 'San Francisco DC',
      purpose: 'Engineering Workstations',
      dns_name: 'desktop-abc123.corp.example.com',
      gateway: '10.50.100.1',
      enriched_at: '2025-05-01T10:00:00Z',
      source: 'Infoblox IPAM'
    },
    // Additional enrichment fields
    asset_criticality: 'high',
    correlation_count: 3,
    threat_intel_score: 85,
    enrichment_summary: {
      status: 'enriched',
      threat_score: 85,
      correlation_count: 3
    }
  },
  {
    id: 'alert-2',
    title: 'Unusual Data Transfer',
    severity: 'high',
    status: StatusType.INVESTIGATING,
    timestamp: '2025-05-01T09:45:00Z',
    description: 'Large volume of data transferred to external IP address',
    affectedAsset: 'SERVER-XYZ789',
    assignedTo: 'jane.smith@example.com',
    tags: [
      { id: "t6", name: "T1048", color: "#44FF44" },
      { id: "t7", name: "Data Transfer", color: "#00FF00" },
      { id: "t8", name: "Network", color: "#008000" }
    ],
    details: {
      source: 'Network Monitoring',
      affectedHost: 'SERVER-XYZ789',
      technique: 'T1048 - Exfiltration Over Alternative Protocol',
      confidence: 0.92,
      description: 'Large data transfer to suspicious external IP detected'
    }
  }
];

const mockMlAnalysis = {
  confidence: 0.95,
  recommendations: [
    'Isolate affected workstation',
    'Review PowerShell logs',
    'Update security policies'
  ],
  performance: {
    accuracy: 0.92,
    precision: 0.89,
    recall: 0.94
  }
};

const mockAssetContext = {
  id: 'DESKTOP-ABC123',
  name: 'Engineering Workstation',
  owner: 'John Doe',
  department: 'Engineering',
  riskScore: 85
};

export const SecurityDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { tenant } = useTenant();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [tags, setTags] = useState<AlertTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [mlAnalysis, setMlAnalysis] = useState(null);
  const [assetContext, setAssetContext] = useState(null);
  const mountedRef = useRef(true);

  // AbortController ref: abort in-flight requests on logout or tenant change.
  // Prevents post-logout state updates from stale async calls that completed
  // after isAuthenticated became false, which caused ghost data and 401 console errors.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Helper function to map API status to StatusType enum
  const mapStatusToEnum = (status: string): StatusType => {
    const statusMap: Record<string, StatusType> = {
      'new': StatusType.ACTIVE,
      'investigating': StatusType.INVESTIGATING,
      'resolved': StatusType.RESOLVED,
      'false_positive': StatusType.RESOLVED,
      'open': StatusType.ACTIVE,
    };
    return statusMap[status] || StatusType.ACTIVE;
  };

  // Load ML analysis from API with cascading fallbacks
  const loadMLAnalysis = async () => {
    // PRIMARY: Try real API
    try {
      console.log('🔄 Attempting primary API...');
      const authToken = secureTokenService.getAuthToken();
      
      const response = await fetch(`${getBaseUrl()}/api/ml/threat-analysis?dashboard=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenant || '',
          'Authorization': `Bearer ${authToken}`,
        },
        signal: AbortSignal.timeout(5000)  // 5 second timeout
      });
      
      if (!response.ok) {
        throw new Error(`API failed: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      setMlAnalysis({
        confidence: data.threat_summary?.risk_score ? data.threat_summary.risk_score / 100 : 0,
        recommendations: data.recommendations || [],
        performance: {
          accuracy: 0.92,
          precision: 0.89,
          recall: 0.94
        },
        mitre_techniques: data.mitre_techniques || [],
        source: 'api',  // ✅ Track source
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ ML analysis loaded from primary API');
      return;  // Success, exit early
      
    } catch (primaryError) {
      console.warn('⚠️ Primary API failed, trying fallback service...', primaryError);
    }
    
    // FALLBACK #1: Try service layer
    try {
      console.log('🔄 Attempting fallback service...');
      const fallbackData = await executiveDashboardService.getMLThreatAnalysis();
      
      setMlAnalysis({
        confidence: fallbackData.risk_score ? fallbackData.risk_score / 100 : 0,
        recommendations: fallbackData.recommendations || [],
        performance: {
          accuracy: 0.92,
          precision: 0.89,
          recall: 0.94
        },
        mitre_techniques: fallbackData.threats?.map((t: any) => ({
          id: t.id,
          name: t.name,
          confidence: t.confidence,
          severity: t.severity
        })) || fallbackData.mitre_techniques || [],
        source: 'fallback',  // ✅ Track source
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ ML analysis loaded from fallback service');
      return;  // Success, exit early
      
    } catch (fallbackError) {
      console.warn('⚠️ Fallback service failed, using mock data...', fallbackError);
    }
    
    // FALLBACK #2: Use mock data (always succeeds)
    console.log('🔄 Using mock data...');
    setMlAnalysis({
      confidence: 0.75,
      recommendations: [
        'Review security logs for anomalies',
        'Investigate source IP addresses',
        'Check for lateral movement indicators'
      ],
      performance: {
        accuracy: 0.92,
        precision: 0.89,
        recall: 0.94
      },
      mitre_techniques: [
        { id: 'T1003', name: 'OS Credential Dumping', confidence: 0.95, severity: 'high' },
        { id: 'T1078', name: 'Valid Accounts', confidence: 0.92, severity: 'high' },
        { id: 'T1055', name: 'Process Injection', confidence: 0.78, severity: 'medium' }
      ],
      source: 'mock',  // ✅ Track source
      timestamp: new Date().toISOString()
    });
    
    console.log('✅ ML analysis loaded from mock data');
  };

  // Load asset context from API (derived from alerts)
  const loadAssetContext = async () => {
    try {
      console.log('🔄 Deriving asset context from alerts...');
      
      // Get the most recent alert to derive asset context
      if (alerts && alerts.length > 0) {
        const recentAlert = alerts[0];
        setAssetContext({
          id: recentAlert.affectedAsset || 'Unknown',
          name: recentAlert.affectedAsset || 'Unknown Asset',
          owner: recentAlert.assignedTo || 'Unassigned',
          department: 'Security Operations',
          riskScore: recentAlert.severity === 'critical' ? 95 : 
                     recentAlert.severity === 'high' ? 75 :
                     recentAlert.severity === 'medium' ? 50 : 25
        });
        console.log('✅ Asset context derived from alerts');
      } else {
        // No alerts, use default context
        setAssetContext({
          id: 'N/A',
          name: 'No asset data',
          owner: 'N/A',
          department: 'N/A',
          riskScore: 0
        });
        console.log('⚠️ No alerts available, using default asset context');
      }
    } catch (err) {
      console.error('❌ Error loading asset context:', err);
      setAssetContext(null);
    }
  };

  // Load alerts from API
  const loadAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching real alerts from API...');
      // Server-side pagination: Fetch only 10 alerts per page
      // Backend handles sorting (severity first, then time) - industry best practice
      // Following Splunk ES, Palo Alto Cortex XDR, CrowdStrike Falcon approach
      const response = await fetchAlerts({ 
        page: 1, 
        page_size: 10,  // Match display page size - no client-side pagination needed
        show_all: false,  // Phase 1: Priority-based visibility (30-40% reduction)
        include_auto_dismissed: false,  // Phase 2: ML auto-filtering (70-80% reduction)
        feedback_provided: false,  // Complete business logic: Only show alerts without feedback (pending work)
        assigned_to: 'unassigned'  // FIX: Show only unclaimed alerts in main dashboard (claimed alerts appear in Work Queue)
      });
      
      // Transform API response to match Alert interface
      // CRITICAL: Preserve ALL fields from backend including P1.15 context
      const transformedAlerts: Alert[] = response.alerts.map((apiAlert: any) => ({
        // Spread ALL backend fields first (includes P1.15 context, enrichment data, etc.)
        ...apiAlert,
        // Then override/normalize specific fields for frontend compatibility
        id: apiAlert.id,
        title: apiAlert.title,
        severity: apiAlert.severity as 'low' | 'medium' | 'high' | 'critical',
        status: mapStatusToEnum(apiAlert.status),
        timestamp: apiAlert.event_time || apiAlert.created_at,
        description: apiAlert.description || '',
        affectedAsset: apiAlert.source || 'Unknown',
        assignedTo: apiAlert.assigned_to || '',
        tags: apiAlert.tags || [],
        details: {
          source: apiAlert.source,
          affectedHost: apiAlert.source_id || 'Unknown',
          technique: apiAlert.mitre_attack_techniques?.[0]?.name || 'Unknown',
          confidence: apiAlert.ml_confidence_score || 0,
          description: apiAlert.llm_summary || apiAlert.description || ''
        }
        // P1.15 context fields (user_context, asset_context, network_context) preserved via spread
        // All enrichment fields preserved via spread
      }));
      
      console.log(`✅ Loaded ${transformedAlerts.length} real alerts`);
      setAlerts(transformedAlerts);
      
    } catch (err) {
      console.error('❌ Error loading alerts:', err);
      setError(err as Error);
      
      // Don't fallback to mock data in production - keep existing alerts or show empty
      console.warn('⚠️ API error - keeping existing alerts or showing empty state');
      // setAlerts(mockAlerts); // REMOVED: Mock fallback causes 400 errors for non-existent alert IDs
      
    } finally {
      setLoading(false);
    }
  };

  // Load all data only when authenticated; re-run and flush stale data on tenant change
  useEffect(() => {
    // Abort any in-flight requests from the previous effect run (logout or tenant switch)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!isAuthenticated) {
      console.log('⏸️ Waiting for authentication before loading data...');
      // Clear data on logout so stale data is never shown after re-login
      setAlerts([]);
      setTags([]);
      setMlAnalysis(null);
      setAssetContext(null);
      return;
    }

    // Create a fresh AbortController for this auth+tenant session
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Flush stale data immediately when tenant changes to prevent cross-tenant data leak
    setAlerts([]);
    setTags([]);
    setMlAnalysis(null);
    setAssetContext(null);

    console.log('✅ User authenticated, loading all data...');
    mountedRef.current = true;
    const loadAllData = async () => {
      if (controller.signal.aborted) return;
      await loadAlerts();
      if (controller.signal.aborted) return;
      loadMLAnalysis();
      if (controller.signal.aborted) return;
      loadAssetContext();
    };
    
    loadAllData();
    
    // Auto-refresh every 30 seconds (alerts only)
    const alertsInterval = setInterval(() => {
      if (controller.signal.aborted || !mountedRef.current) return;
      console.log('🔄 Background refresh: updating alerts...');
      loadAlerts();
    }, 30000);
    
    // Refresh ML analysis every 5 minutes
    const mlInterval = setInterval(() => {
      if (controller.signal.aborted || !mountedRef.current) return;
      console.log('🔄 Background refresh: updating ML analysis...');
      loadMLAnalysis();
    }, 300000);
    
    return () => {
      controller.abort();
      mountedRef.current = false;
      clearInterval(alertsInterval);
      clearInterval(mlInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, tenant]);
  
  // Load asset context when alerts actually change (not just length)
  const alertIdsRef = useRef<string>('');
  useEffect(() => {
    if (alerts.length === 0) return;
    
    const currentIds = alerts.map(a => a.id).sort().join(',');
    if (alertIdsRef.current === currentIds) return;
    
    alertIdsRef.current = currentIds;
    loadAssetContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts]);

  // Refresh function for manual refresh
  const refreshAlerts = async () => {
    await Promise.all([
      loadAlerts(),
      loadMLAnalysis(),
      loadAssetContext()
    ]);
  };

  return (
    <SecurityDataContext.Provider value={{ alerts, tags, loading, error, mlAnalysis, assetContext, refreshAlerts }}>
      {children}
    </SecurityDataContext.Provider>
  );
};

export const useSecurityData = () => useContext(SecurityDataContext);
